import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getClients, getCompanies, getInvoices } from '../../firebase/database';
import { LogisticsInvoice } from '../../types';
import { 
  generateClientOutstandingPDF, 
  generateCompanyOutstandingPDF, 
  generateGrossProfitPDF,
  openPDFInNewTab 
} from '../../services/reportsPdfService';
import toast, { Toaster } from 'react-hot-toast';

//change

interface ClientOutstanding {
  name: string;
  outstanding: number;
  clientType?: 'import' | 'export' | 'both';
}

interface CompanyOutstanding {
  name: string;
  outstanding: number;
}

interface GrossProfitEntry {
  clientName: string;
  sale: number;
  cost: number;
  grossProfit: number;
  deduction: number;
  netProfit: number;
}

const Reports: React.FC = () => {
  const { currentMode } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clientOutstandings, setClientOutstandings] = useState<ClientOutstanding[]>([]);
  const [companyOutstandings, setCompanyOutstandings] = useState<CompanyOutstanding[]>([]);
  const [grossProfitEntries, setGrossProfitEntries] = useState<GrossProfitEntry[]>([]);
  const [generatingPDF, setGeneratingPDF] = useState<string | null>(null);
  const [approvedInvoicesState, setApprovedInvoicesState] = useState<LogisticsInvoice[]>([]);
  const [filteredApprovedInvoices, setFilteredApprovedInvoices] = useState<LogisticsInvoice[]>([]);
  const [showClientInvoicesModal, setShowClientInvoicesModal] = useState(false);
  const [selectedClientForModal, setSelectedClientForModal] = useState<string | null>(null);
  const [clientInvoicesForModal, setClientInvoicesForModal] = useState<LogisticsInvoice[]>([]);
  const [hoveredPoint, setHoveredPoint] = useState<{ i: number; x: number; y: number; series: 'Sale' | 'Cost' | 'Gross Profit' | 'Net Profit'; value: number; label: string } | null>(null);
  const [grossProfitStartDate, setGrossProfitStartDate] = useState<string>('');
  const [grossProfitEndDate, setGrossProfitEndDate] = useState<string>('');
  const [grossProfitDateError, setGrossProfitDateError] = useState<string | null>(null);
  const [grossProfitRangeInitialized, setGrossProfitRangeInitialized] = useState(false);
  
  // Accordion states
  const [clientAccordionOpen, setClientAccordionOpen] = useState(true);
  const [companyAccordionOpen, setCompanyAccordionOpen] = useState(false);
  const [grossProfitAccordionOpen, setGrossProfitAccordionOpen] = useState(false);

  const formatDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseInputDate = (value: string | null, endOfDay = false) => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if ([year, month, day].some(num => Number.isNaN(num))) return null;
    if (endOfDay) {
      return new Date(year, (month ?? 1) - 1, day ?? 1, 23, 59, 59, 999);
    }
    return new Date(year, (month ?? 1) - 1, day ?? 1, 0, 0, 0, 0);
  };

  const loadReportsData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load clients and their outstanding
      const clients = await getClients();
      const clientData: ClientOutstanding[] = clients.map(client => ({
        name: client.name,
        outstanding: client.outstanding || 0,
        clientType: client.clientType
      }));
      setClientOutstandings(clientData);

      // Load companies and their outstanding
      const companies = await getCompanies();
      const companyData: CompanyOutstanding[] = companies.map(company => ({
        name: company.name,
        outstanding: company.outstanding || 0
      }));
      setCompanyOutstandings(companyData);

      // Load approved invoices for gross profit
      if (currentMode === 'authorizer') {
        const allInvoices = await getInvoices();
        const approvedInvoices = allInvoices.filter(inv => inv.status === 'approved');
        setApprovedInvoicesState(approvedInvoices);
      } else {
        setApprovedInvoicesState([]);
        setFilteredApprovedInvoices([]);
        setGrossProfitEntries([]);
      }
    } catch (error) {
      console.error('Error loading reports data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentMode]);

  useEffect(() => {
    loadReportsData();
  }, [loadReportsData]);

  const handleGenerateClientPDF = async () => {
    try {
      setGeneratingPDF('client');
      const pdf = await generateClientOutstandingPDF(clientOutstandings);
      openPDFInNewTab(pdf, `Client_Outstanding_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Client Outstanding PDF generated successfully!');
    } catch (error) {
      console.error('Error generating client outstanding PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handleGenerateCompanyPDF = async () => {
    try {
      setGeneratingPDF('company');
      const pdf = await generateCompanyOutstandingPDF(companyOutstandings);
      openPDFInNewTab(pdf, `Company_Outstanding_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Company Outstanding PDF generated successfully!');
    } catch (error) {
      console.error('Error generating company outstanding PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handleGenerateGrossProfitPDF = async () => {
    try {
      setGeneratingPDF('grossProfit');
      const pdf = await generateGrossProfitPDF(grossProfitEntries);
      openPDFInNewTab(pdf, `Gross_Profit_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Gross Profit PDF generated successfully!');
    } catch (error) {
      console.error('Error generating gross profit PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(null);
    }
  };

  useEffect(() => {
    if (currentMode !== 'authorizer') {
      setGrossProfitStartDate('');
      setGrossProfitEndDate('');
      setGrossProfitRangeInitialized(false);
      return;
    }

    if (grossProfitRangeInitialized || approvedInvoicesState.length === 0) {
      return;
    }

    const validYears = approvedInvoicesState.reduce<number[]>((acc, inv) => {
      if (!inv.date) return acc;
      const date = new Date(inv.date);
      if (Number.isNaN(date.getTime())) return acc;
      const year = date.getFullYear();
      if (!acc.includes(year)) acc.push(year);
      return acc;
    }, []);

    if (validYears.length === 0) return;

    const latestYear = Math.max(...validYears);
    setGrossProfitStartDate(formatDateInput(new Date(latestYear, 0, 1)));
    setGrossProfitEndDate(formatDateInput(new Date(latestYear, 11, 31)));
    setGrossProfitRangeInitialized(true);
  }, [approvedInvoicesState, currentMode, grossProfitRangeInitialized]);

  useEffect(() => {
    if (currentMode !== 'authorizer') {
      setGrossProfitEntries([]);
      setFilteredApprovedInvoices([]);
      setGrossProfitDateError(null);
      return;
    }

    const start = parseInputDate(grossProfitStartDate);
    const end = parseInputDate(grossProfitEndDate, true);

    if (start && end && start > end) {
      setGrossProfitDateError('Start date cannot be after end date.');
      setGrossProfitEntries([]);
      setFilteredApprovedInvoices([]);
      return;
    }

    setGrossProfitDateError(null);

    const filteredInvoices = approvedInvoicesState.filter(inv => {
      if (!inv.date) return !start && !end;
      const invoiceDate = new Date(inv.date);
      if (Number.isNaN(invoiceDate.getTime())) return !start && !end;

      if (start && invoiceDate < start) return false;
      if (end && invoiceDate > end) return false;

      return true;
    });

    setFilteredApprovedInvoices(filteredInvoices);

    const clientMap = new Map<string, { sale: number; cost: number; deduction: number }>();

    filteredInvoices.forEach(invoice => {
      const clientName = invoice.clientName;

      const sale = invoice.saleItems && Array.isArray(invoice.saleItems)
        ? invoice.saleItems.reduce((sum, item) => {
            const amount = item?.amount || 0;
            if (item?.currency && item.currency !== 'PKR') {
              const rate = Number(invoice.exchangeRate) || 1;
              return sum + amount * rate;
            }
            return sum + amount;
          }, 0)
        : (() => {
            const base = invoice.total || 0;
            if (invoice.currency && invoice.currency !== 'PKR') {
              const rate = Number(invoice.exchangeRate) || 1;
              return base * rate;
            }
            return base;
          })();

      const cost = invoice.costItems && Array.isArray(invoice.costItems)
        ? invoice.costItems.reduce((sum, item) => {
            const amount = item?.amount || 0;
            if (item?.currency && item.currency !== 'PKR') {
              const rate = Number(invoice.exchangeRate) || 1;
              return sum + amount * rate;
            }
            return sum + amount;
          }, 0)
        : 0;

      const deduction = invoice.discount || 0;

      if (clientMap.has(clientName)) {
        const existing = clientMap.get(clientName)!;
        existing.sale += sale;
        existing.cost += cost;
        existing.deduction += deduction;
      } else {
        clientMap.set(clientName, { sale, cost, deduction: deduction });
      }
    });

    const profitData: GrossProfitEntry[] = Array.from(clientMap.entries()).map(([clientName, data]) => {
      const grossProfit = data.sale - data.cost;
      const netProfit = grossProfit - data.deduction;
      return {
        clientName,
        sale: data.sale,
        cost: data.cost,
        grossProfit,
        deduction: data.deduction,
        netProfit
      };
    });

    profitData.sort((a, b) => a.clientName.localeCompare(b.clientName));

    setGrossProfitEntries(profitData);
  }, [approvedInvoicesState, currentMode, grossProfitStartDate, grossProfitEndDate]);

  const availableGrossProfitYears = useMemo(() => {
    const years = new Set<number>();
    approvedInvoicesState.forEach(inv => {
      if (!inv.date) return;
      const date = new Date(inv.date);
      if (Number.isNaN(date.getTime())) return;
      years.add(date.getFullYear());
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [approvedInvoicesState]);

  const quickYearValue = useMemo(() => {
    const start = parseInputDate(grossProfitStartDate);
    const end = parseInputDate(grossProfitEndDate);
    if (!start || !end) return '';
    if (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === 0 &&
      start.getDate() === 1 &&
      end.getMonth() === 11 &&
      end.getDate() === 31
    ) {
      return String(start.getFullYear());
    }
    return '';
  }, [grossProfitStartDate, grossProfitEndDate]);

  const grossProfitRangeLabel = useMemo(() => {
    const start = parseInputDate(grossProfitStartDate);
    const end = parseInputDate(grossProfitEndDate);
    const formatDisplay = (date: Date) => date.toLocaleDateString();

    if (!start && !end) return 'All approved invoices';
    if (start && end) return `${formatDisplay(start)} — ${formatDisplay(end)}`;
    if (start) return `From ${formatDisplay(start)}`;
    return `Up to ${formatDisplay(end as Date)}`;
  }, [grossProfitStartDate, grossProfitEndDate]);

  const handleGrossProfitQuickYearSelect = (year: number) => {
    setGrossProfitStartDate(formatDateInput(new Date(year, 0, 1)));
    setGrossProfitEndDate(formatDateInput(new Date(year, 11, 31)));
  };

  const handleResetGrossProfitRange = () => {
    setGrossProfitStartDate('');
    setGrossProfitEndDate('');
  };

  const openClientInvoicesModal = (clientName: string) => {
    const start = parseInputDate(grossProfitStartDate);
    const end = parseInputDate(grossProfitEndDate, true);

    const invoices = filteredApprovedInvoices
      .filter(inv => inv.clientName === clientName)
      .filter(inv => {
        if (!inv.date) return !start && !end;
        const invoiceDate = new Date(inv.date);
        if (Number.isNaN(invoiceDate.getTime())) return !start && !end;
        if (start && invoiceDate < start) return false;
        if (end && invoiceDate > end) return false;
        return true;
      })
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateA - dateB;
      });
    setSelectedClientForModal(clientName);
    setClientInvoicesForModal(invoices);
    setShowClientInvoicesModal(true);
  };

  const computeInvoiceSale = (inv: any): number => {
    if (inv.saleItems && Array.isArray(inv.saleItems)) {
      return inv.saleItems.reduce((sum: number, item: any) => {
        const amount = item?.amount || 0;
        if (item?.currency && item.currency !== 'PKR') {
          const rate = Number(inv.exchangeRate) || 1;
          return sum + amount * rate;
        }
        return sum + amount;
      }, 0);
    }
    const base = inv.total || 0;
    if (inv.currency && inv.currency !== 'PKR') {
      const rate = Number(inv.exchangeRate) || 1;
      return base * rate;
    }
    return base;
  };

  const computeInvoiceCost = (inv: any): number => {
    if (inv.costItems && Array.isArray(inv.costItems)) {
      return inv.costItems.reduce((sum: number, item: any) => {
        const amount = item?.amount || 0;
        if (item?.currency && item.currency !== 'PKR') {
          const rate = Number(inv.exchangeRate) || 1;
          return sum + amount * rate;
        }
        return sum + amount;
      }, 0);
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen relative flex items-center justify-center">
        {/* Background Image with Blur */}
        <div 
          className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/background.png)',
            filter: 'blur(0px)',
            transform: 'scale(1.1)'
          }}
        />
        <div className="fixed inset-0 bg-gray-900 bg-opacity-30" />
        
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white drop-shadow-lg">Loading reports...</p>
        </div>
      </div>
    );
  }

  const totalClientOutstanding = clientOutstandings.reduce((sum, client) => sum + client.outstanding, 0);
  const totalCompanyOutstanding = companyOutstandings.reduce((sum, company) => sum + company.outstanding, 0);
  const totalSale = grossProfitEntries.reduce((sum, entry) => sum + entry.sale, 0);
  const totalCost = grossProfitEntries.reduce((sum, entry) => sum + entry.cost, 0);
  const totalDeduction = grossProfitEntries.reduce((sum, entry) => sum + entry.deduction, 0);
  const totalGrossProfit = totalSale - totalCost;
  const totalNetProfit = totalGrossProfit - totalDeduction;

  return (
    <div className="min-h-screen relative py-8">
      {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/background.png)',
          filter: 'blur(0px)',
          transform: 'scale(1.1)'
        }}
      />
      
      {/* Overlay to darken the background */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-30" />
      
      {/* Content */}
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white drop-shadow-lg">Reports</h1>
            <p className="text-white/90 drop-shadow-md">Financial reports and outstanding balances</p>
          </div>

          {/* Client-wise Outstanding */}
          <div className="mb-6">
            <div className="bg-white shadow-xl rounded-2xl border-2 border-gray-300 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-300 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
                   onClick={() => setClientAccordionOpen(!clientAccordionOpen)}>
                <div className="flex items-center space-x-3">
                  <button className="text-gray-600 hover:text-gray-900 transition-transform duration-200"
                          style={{ transform: clientAccordionOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Clientwise Outstanding (Revenue)</h2>
                    <p className="text-sm text-gray-600">Total: PKR {Math.round(totalClientOutstanding).toLocaleString()}</p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGenerateClientPDF();
                  }}
                  disabled={generatingPDF === 'client' || clientOutstandings.length === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingPDF === 'client' ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>View PDF</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Accordion Content */}
              <div className={`transition-all duration-300 ease-in-out ${clientAccordionOpen ? 'max-h-[600px]' : 'max-h-0'} overflow-hidden`}>
                <div className="p-6">
                  <div className="overflow-x-auto overflow-y-auto max-h-[500px] border border-gray-200 rounded-lg">
                    <table className="min-w-full">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Client Name</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Client Type</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Outstanding Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {clientOutstandings.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-600">No clients found</td>
                          </tr>
                        ) : (
                          clientOutstandings.map((client, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{client.name}</td>
                              <td className="px-6 py-4 text-sm text-gray-700 capitalize">{client.clientType || '—'}</td>
                              <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                                PKR {Math.round(client.outstanding).toLocaleString()}
                              </td>
                            </tr>
                          ))
                        )}
                        {clientOutstandings.length > 0 && (
                          <tr className="bg-orange-100 font-bold sticky bottom-0">
                            <td className="px-6 py-4 text-sm text-gray-900" colSpan={2}>Total Outstanding</td>
                            <td className="px-6 py-4 text-sm text-right text-gray-900">
                              PKR {Math.round(totalClientOutstanding).toLocaleString()}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Company-wise Outstanding */}
          <div className="mb-6">
            <div className="bg-white shadow-xl rounded-2xl border-2 border-gray-300 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-300 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
                   onClick={() => setCompanyAccordionOpen(!companyAccordionOpen)}>
                <div className="flex items-center space-x-3">
                  <button className="text-gray-600 hover:text-gray-900 transition-transform duration-200"
                          style={{ transform: companyAccordionOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Companywise Outstanding (Expenses)</h2>
                    <p className="text-sm text-gray-600">Total: PKR {Math.round(totalCompanyOutstanding).toLocaleString()}</p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGenerateCompanyPDF();
                  }}
                  disabled={generatingPDF === 'company' || companyOutstandings.length === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingPDF === 'company' ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>View PDF</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Accordion Content */}
              <div className={`transition-all duration-300 ease-in-out ${companyAccordionOpen ? 'max-h-[600px]' : 'max-h-0'} overflow-hidden`}>
                <div className="p-6">
                  <div className="overflow-x-auto overflow-y-auto max-h-[500px] border border-gray-200 rounded-lg">
                    <table className="min-w-full">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Company Name</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Outstanding Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {companyOutstandings.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-600">No companies found</td>
                          </tr>
                        ) : (
                          companyOutstandings.map((company, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{company.name}</td>
                              <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                                PKR {Math.round(company.outstanding).toLocaleString()}
                              </td>
                            </tr>
                          ))
                        )}
                        {companyOutstandings.length > 0 && (
                          <tr className="bg-orange-100 font-bold sticky bottom-0">
                            <td className="px-6 py-4 text-sm text-gray-900">Total Outstanding</td>
                            <td className="px-6 py-4 text-sm text-right text-gray-900">
                              PKR {Math.round(totalCompanyOutstanding).toLocaleString()}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gross Profit - Only for Authorizer */}
          {currentMode === 'authorizer' && (
            <div className="mb-6">
              <div className="bg-white shadow-xl rounded-2xl border-2 border-gray-300 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-300 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
                     onClick={() => setGrossProfitAccordionOpen(!grossProfitAccordionOpen)}>
                  <div className="flex items-center space-x-3">
                    <button className="text-gray-600 hover:text-gray-900 transition-transform duration-200"
                            style={{ transform: grossProfitAccordionOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Profit Analysis</h2>
                      <p className="text-sm text-gray-600">Client-wise | Profit: PKR {Math.round(totalGrossProfit).toLocaleString()} | Net Profit: PKR {Math.round(totalNetProfit).toLocaleString()}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateGrossProfitPDF();
                    }}
                    disabled={generatingPDF === 'grossProfit' || grossProfitEntries.length === 0}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingPDF === 'grossProfit' ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span>View PDF</span>
                      </>
                    )}
                  </button>
                </div>
                
                {/* Accordion Content */}
                <div className={`transition-all duration-300 ease-in-out ${grossProfitAccordionOpen ? 'max-h-[600px]' : 'max-h-0'} overflow-hidden`}>
                  <div className="p-6">
                    <div className="mb-4 space-y-4">
                      <div className="flex flex-col md:flex-row md:items-end gap-4">
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Start Date</label>
                          <input
                            type="date"
                            value={grossProfitStartDate}
                            onChange={(e) => setGrossProfitStartDate(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">End Date</label>
                          <input
                            type="date"
                            value={grossProfitEndDate}
                            onChange={(e) => setGrossProfitEndDate(e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                          />
                        </div>
                        <div className="flex flex-col md:w-48">
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Quick Year</label>
                          <select
                            value={quickYearValue}
                            onChange={(e) => {
                              if (e.target.value === '') return;
                              const year = Number(e.target.value);
                              if (!Number.isNaN(year) && year !== 0) {
                                handleGrossProfitQuickYearSelect(year);
                              }
                            }}
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                          >
                            <option value="">Custom range</option>
                            {availableGrossProfitYears.map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={handleResetGrossProfitRange}
                            className="mt-2 text-xs font-semibold text-green-600 hover:text-green-700"
                          >
                            Clear range
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <span className="text-sm text-gray-600">Period: <span className="font-semibold text-gray-800">{grossProfitRangeLabel}</span></span>
                        {grossProfitDateError && (
                          <span className="text-sm font-semibold text-red-600">{grossProfitDateError}</span>
                        )}
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg">
                      <div className="overflow-x-auto">
                        <div className="max-h-[420px] overflow-y-auto">
                          <table className="min-w-full">
                            <thead className="bg-gray-100 sticky top-0 z-10">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Sale</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Cost</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Gross Profit</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Deduction</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Net Profit</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {grossProfitEntries.length === 0 ? (
                                <tr>
                                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-600">No approved invoices found</td>
                                </tr>
                              ) : (
                                grossProfitEntries.map((entry, index) => (
                                  <tr
                                    key={index}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => openClientInvoicesModal(entry.clientName)}
                                    title="View client invoices and trend"
                                  >
                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{entry.clientName}</td>
                                    <td className="px-6 py-4 text-sm text-right font-semibold text-green-600">
                                      PKR {Math.round(entry.sale).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-right font-semibold text-red-600">
                                      PKR {Math.round(entry.cost).toLocaleString()}
                                    </td>
                                    <td className={`px-6 py-4 text-sm text-right font-bold ${entry.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      PKR {Math.round(entry.grossProfit).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-right font-semibold text-orange-600">
                                      PKR {Math.round(entry.deduction).toLocaleString()}
                                    </td>
                                    <td className={`px-6 py-4 text-sm text-right font-bold ${entry.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      PKR {Math.round(entry.netProfit).toLocaleString()}
                                    </td>
                                  </tr>
                                ))
                              )}
                              {grossProfitEntries.length > 0 && (
                                <tr className="bg-green-100 font-bold sticky bottom-0 z-10">
                                  <td className="px-6 py-4 text-sm text-gray-900">Total</td>
                                  <td className="px-6 py-4 text-sm text-right text-green-600">
                                    PKR {Math.round(totalSale).toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-right text-red-600">
                                    PKR {Math.round(totalCost).toLocaleString()}
                                  </td>
                                  <td className={`px-6 py-4 text-sm text-right ${totalGrossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    PKR {Math.round(totalGrossProfit).toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-right text-orange-600">
                                    PKR {Math.round(totalDeduction).toLocaleString()}
                                  </td>
                                  <td className={`px-6 py-4 text-sm text-right ${totalNetProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    PKR {Math.round(totalNetProfit).toLocaleString()}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Toaster position="top-right" />
      {/* Client Invoices Modal */}
      {showClientInvoicesModal && selectedClientForModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50">
          <div className="h-full overflow-y-auto flex items-center justify-center p-4">
            <div className="relative w-full max-w-5xl mx-auto p-5 border shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{selectedClientForModal} — Invoices & Profit/Loss Trend</h3>
                <button onClick={() => setShowClientInvoicesModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Profit/Loss Chart */}
              <div className="mb-4 relative">
                {(() => {
                  const data = clientInvoicesForModal.map((inv, idx) => {
                    const sale = computeInvoiceSale(inv);
                    const cost = computeInvoiceCost(inv);
                    const deduction = inv.discount || 0;
                    const grossProfit = sale - cost;
                    const netProfit = grossProfit - deduction;
                    return {
                      label: inv.invoiceNumber || `#${idx + 1}`,
                      date: new Date(inv.date).toLocaleDateString(),
                      sale,
                      cost,
                      grossProfit,
                      deduction,
                      netProfit
                    };
                  });

                  const values = data.flatMap(d => [d.sale, d.cost, d.grossProfit, d.netProfit, 0]);
                  const minVal = Math.min(...values);
                  const maxVal = Math.max(...values);

                  const width = 900;
                  const height = 320;
                  const margin = { top: 16, right: 20, bottom: 56, left: 80 };
                  const innerW = width - margin.left - margin.right;
                  const innerH = height - margin.top - margin.bottom;

                  const count = Math.max(data.length, 1);
                  const band = innerW / count; // band per category
                  const groupWidth = Math.min(50, Math.max(20, band * 0.5)); // width allocated for series bars
                  const barWidth = Math.max(5, Math.floor(groupWidth / 4) - 2);
                  const xCenter = (i: number) => band * i + band / 2;
                  const yPos = (v: number) => {
                    if (maxVal === minVal) return innerH / 2;
                    const t = (v - minVal) / (maxVal - minVal);
                    return innerH - t * innerH;
                  };

                  const series: Array<{ key: 'sale' | 'cost' | 'grossProfit' | 'netProfit'; label: 'Sale' | 'Cost' | 'Gross Profit' | 'Net Profit'; color: string }>
                    = [
                      { key: 'sale', label: 'Sale', color: '#2563eb' },
                      { key: 'cost', label: 'Cost', color: '#ef4444' },
                      { key: 'grossProfit', label: 'Gross Profit', color: '#10b981' },
                      { key: 'netProfit', label: 'Net Profit', color: '#059669' }
                    ];

                  const yTicks = 5;
                  const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) => minVal + (i * (maxVal - minVal)) / yTicks);

                  return (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-800">Profit/Loss (Grouped Bars)</div>
                        <div className="text-xs text-gray-500">Per approved invoice</div>
                      </div>
                      <svg width={width} height={height} className="w-full border rounded bg-white">
                        <g transform={`translate(${margin.left},${margin.top})`}>
                          {/* Grid */}
                          {yTickVals.map((t, idx) => (
                            <line key={idx} x1={0} x2={innerW} y1={yPos(t)} y2={yPos(t)} stroke="#eef2f7" />
                          ))}
                          {/* Zero baseline */}
                          <line x1={0} x2={innerW} y1={yPos(0)} y2={yPos(0)} stroke="#c7cdd6" />

                          {/* Bars */}
                          {data.map((d, i) => (
                            <g key={i} transform={`translate(${xCenter(i)},0)`}>
                              {series.map((s, si) => {
                                const val = d[s.key];
                                const x = -groupWidth / 2 + si * (barWidth + 4);
                                const y0 = yPos(0);
                                const y1 = yPos(val);
                                const y = Math.min(y0, y1);
                                const h = Math.abs(y1 - y0);
                                return (
                                  <rect
                                    key={s.key}
                                    x={x}
                                    y={y}
                                    width={barWidth}
                                    height={Math.max(1, h)}
                                    fill={s.color}
                                    opacity={s.key === 'grossProfit' || s.key === 'netProfit' ? 0.85 : 0.65}
                                    rx={2}
                                    onMouseEnter={(e) => {
                                      const svg = (e.target as SVGRectElement).closest('svg');
                                      if (!svg) return;
                                      const rect = svg.getBoundingClientRect();
                                      const cx = (e.clientX - rect.left);
                                      const cy = (e.clientY - rect.top);
                                      setHoveredPoint({ i, x: cx, y: cy, series: s.label, value: val, label: `${d.label} (${d.date})` });
                                    }}
                                    onMouseLeave={() => setHoveredPoint(null)}
                                  />
                                );
                              })}
                            </g>
                          ))}

                          {/* Axes labels */}
                          {yTickVals.map((t, idx) => (
                            <text key={idx} x={-10} y={yPos(t)} textAnchor="end" alignmentBaseline="middle" fontSize="11" fill="#6b7280">
                              {`PKR ${Math.round(t).toLocaleString()}`}
                            </text>
                          ))}
                          {data.map((d, i) => (
                            <g key={i} transform={`translate(${xCenter(i)},${innerH})`}>
                              <line y1={0} y2={6} stroke="#9ca3af" />
                              <text transform="translate(0,22) rotate(-30)" textAnchor="end" fontSize="11" fill="#6b7280">
                                {d.label}
                              </text>
                            </g>
                          ))}

                          {/* Legend */}
                          <g transform={`translate(${Math.max(0, innerW - 300)},6)`}>
                            <rect x={0} y={-2} width={300} height={50} fill="#ffffff" stroke="#e5e7eb" rx={6} />
                            <g transform="translate(10,8)">
                              <rect x={0} y={-5} width={18} height={10} fill="#2563eb" />
                              <text x={26} y={2} fontSize="11" fill="#374151">Sale</text>
                            </g>
                            <g transform="translate(10,22)">
                              <rect x={0} y={-5} width={18} height={10} fill="#ef4444" />
                              <text x={26} y={2} fontSize="11" fill="#374151">Cost</text>
                            </g>
                            <g transform="translate(90,8)">
                              <rect x={0} y={-5} width={18} height={10} fill="#10b981" />
                              <text x={26} y={2} fontSize="11" fill="#374151">Gross Profit</text>
                            </g>
                            <g transform="translate(90,22)">
                              <rect x={0} y={-5} width={18} height={10} fill="#059669" />
                              <text x={26} y={2} fontSize="11" fill="#374151">Net Profit</text>
                            </g>
                          </g>
                        </g>
                      </svg>
                      {hoveredPoint && (
                        <div
                          className="pointer-events-none absolute bg-white border border-gray-200 rounded shadow px-2 py-1 text-xs text-gray-700"
                          style={{ left: Math.max(8, hoveredPoint.x - 70), top: Math.max(8, hoveredPoint.y - 52) }}
                        >
                          <div className="font-medium">{hoveredPoint.label}</div>
                          <div><span className="text-gray-500">{hoveredPoint.series}:</span> PKR {Math.round(hoveredPoint.value).toLocaleString()}</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Invoices Table */}
              <div className="overflow-x-auto max-h-[420px] border rounded">
                <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Invoice #</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Sale</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Cost</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Gross Profit</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Deduction</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Net Profit</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clientInvoicesForModal.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-3 py-4 text-center text-sm text-gray-600">No invoices</td>
                      </tr>
                    ) : (
                      clientInvoicesForModal.map((inv) => {
                        const sale = computeInvoiceSale(inv);
                        const cost = computeInvoiceCost(inv);
                        const deduction = inv.discount || 0;
                        const grossProfit = sale - cost;
                        const netProfit = grossProfit - deduction;
                        return (
                          <tr key={inv.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-gray-700">{new Date(inv.date).toLocaleDateString()}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{inv.invoiceNumber}</td>
                            <td className="px-3 py-2 text-sm text-right text-green-600">PKR {Math.round(sale).toLocaleString()}</td>
                            <td className="px-3 py-2 text-sm text-right text-red-600">PKR {Math.round(cost).toLocaleString()}</td>
                            <td className={`px-3 py-2 text-sm text-right font-semibold ${grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>PKR {Math.round(grossProfit).toLocaleString()}</td>
                            <td className="px-3 py-2 text-sm text-right text-orange-600">PKR {Math.round(deduction).toLocaleString()}</td>
                            <td className={`px-3 py-2 text-sm text-right font-semibold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>PKR {Math.round(netProfit).toLocaleString()}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{inv.status}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-end">
                <button onClick={() => setShowClientInvoicesModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
